/**
 * Beside the dataset list when a live dataset is picked: which version the run
 * uses, like picking a version of any dataset. Fresh traces (the default) are
 * pulled when the run starts; or pick a version pulled before, to run on the
 * same traces as an earlier run. A duplicate starts on its original's version.
 */

import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { ErrorState, Skeleton } from "@/components/ui/states";
import { useDatasetSource } from "@/features/datasets/hooks";
import { PeriodPicker } from "@/features/traces/period-picker";
import { formatCount, formatDateTime } from "@/lib/format";
import type { NewExperiment } from "../use-new-experiment";
import { versionName } from "./pull-text";

const FIRST = 5;

export function LiveVersionPicker({ flow }: { flow: NewExperiment }) {
  const live = flow.liveVersion;
  const { query, versions, chosen } = live;
  const [all, setAll] = useState(false);
  // The chosen version always shows, even when it is older than the first few.
  const shown = all ? versions : versions.filter((v, i) => i < FIRST || v.version === chosen?.version);
  const copied = flow.copyOf && live.version != null && live.version === live.original;
  return (
    <section
      aria-label="Version"
      className="flex flex-col gap-3 rounded-[9px] border border-line-soft bg-well p-4"
    >
      <p className="text-xs text-muted">A live dataset: pick the traces this run scores.</p>
      {copied && (
        <p className="rounded-md bg-subtle px-3 py-2 text-xs text-body">
          Same version as <b className="font-semibold">{flow.copyOf}</b>. Pick fresh traces to run on new
          ones.
        </p>
      )}
      <div role="radiogroup" aria-label="Traces to run on" className="flex flex-col gap-1.5">
        <Row
          checked={live.fresh}
          onPick={() => live.pick("fresh")}
          note="Saved as a new version when the run starts"
        >
          Fresh traces — pulled when the run starts
        </Row>
        {live.fresh && <FreshOptions flow={flow} />}
        {versions.length > 0 && <span className="mt-2 text-[11px] font-semibold text-faint">Versions</span>}
        {shown.map((v) => (
          <Row
            key={v.id}
            checked={v.version === chosen?.version}
            onPick={() => live.pick(v.version)}
            note={`pulled ${formatDateTime(v.created_at)}`}
          >
            {versionName(v)}
          </Row>
        ))}
      </div>
      {!all && versions.length > shown.length && (
        <button type="button" className="link self-start text-xs" onClick={() => setAll(true)}>
          Show all {versions.length} versions
        </button>
      )}
      {query.isPending && <VersionsLoading flow={flow} />}
      {query.isError && (
        <div className="flex flex-col items-start gap-2">
          <ErrorState error={query.error} what="the dataset's versions" />
          <Button size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * Rows the size of the versions to come, so nothing below jumps when they load:
 * as many as the dataset list says it has, up to the first few.
 */
function VersionsLoading({ flow }: { flow: NewExperiment }) {
  const known = flow.dataset?.versions?.length ?? (flow.dataset?.latest_version ? FIRST : 0);
  const rows = Math.min(FIRST, known);
  if (rows === 0) return <Skeleton rows={1} label="Loading versions" />;
  return (
    <div role="status" aria-label="Loading versions" className="-mt-1.5 flex flex-col gap-1.5">
      <span className="mt-2 h-4 w-16 animate-pulse rounded bg-chip" />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-[58px] animate-pulse rounded-[9px] border border-line-soft bg-chip/60" />
      ))}
    </div>
  );
}

/** Fresh traces: the period, how they are picked, and "Pull next" once there are earlier versions. */
function FreshOptions({ flow }: { flow: NewExperiment }) {
  const live = flow.liveVersion;
  const limit = useDatasetSource(flow.datasetId ?? "", !!flow.datasetId).data?.limit;
  const count = limit ? formatCount(limit) : "";
  return (
    <div className="ml-7 flex flex-col gap-2.5 pb-1">
      {!live.next && <PeriodPicker id="ne-period" value={flow.period} onChange={flow.setPeriod} />}
      {!live.next && (
        <span className="text-xs text-muted">
          Takes the first {count || "set number of"} traces from the start of the period
        </span>
      )}
      {live.latest != null && (
        <label className="flex items-start gap-2 text-[13px] text-ink">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-ink"
            checked={live.next}
            onChange={(e) => live.setNext(e.target.checked)}
          />
          <span className="flex flex-col">
            Pull next{count && ` ${count}`} (continue after v{live.latest})
            <span className="text-xs text-muted">
              Starts right after the last trace of v{live.latest}, up to now, reaching back at most 92 days.
              Ignores the period start.
            </span>
          </span>
        </label>
      )}
    </div>
  );
}

function Row({
  checked,
  onPick,
  note,
  children,
}: {
  checked: boolean;
  onPick: () => void;
  note: string;
  children: ReactNode;
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-[9px] border px-3 py-2.5 transition-[border-color,background-color] ${
        checked
          ? "border-link bg-run-bg/40 shadow-[0_0_0_1px_var(--color-link)]"
          : "border-line hover:bg-subtle"
      }`}
    >
      <input
        type="radio"
        name="live-version"
        className="mt-0.5 size-4 shrink-0 accent-link"
        checked={checked}
        onChange={onPick}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[13px] font-semibold text-ink">{children}</span>{" "}
        <span className="text-xs text-muted">{note}</span>
      </span>
    </label>
  );
}
